export interface WorkCard {
  kind: string;
  period: string;
  name: string;
  tagline: string;
  chips: readonly string[];
  href: string | null;
}

export interface CaseStudyMetric {
  value: string;
  label: string;
}

export interface CaseStudyBlock {
  heading: string;
  body: string;
  bullets?: readonly string[];
}

export interface CaseStudy {
  kind: string;
  title: string;
  tagline: string;
  meta: string;
  metrics: readonly CaseStudyMetric[];
  blocks: readonly CaseStudyBlock[];
  stack: readonly string[];
}

export const workCards: readonly WorkCard[] = [
  {
    kind: 'Enterprise SaaS',
    period: '2022 — 2025',
    name: 'ValueBlue',
    tagline:
      'A BPMN 2.0 process-modeling editor and AI assistant, built for enterprise architects.',
    chips: ['TypeScript', 'React', 'bpmn-js', 'Single-spa'],
    href: '/work/valueblue',
  },
  {
    kind: 'Healthcare',
    period: '2021 — 2025',
    name: 'Ishi Health',
    tagline:
      'A zero-downtime database migration and a 3× faster app for a healthcare platform.',
    chips: ['TypeScript', 'PostgreSQL', 'TypeORM', 'Vite'],
    href: '/work/ishi-health',
  },
  {
    kind: 'Product · Founder',
    period: '2024 — present',
    name: 'edulinks.ng',
    tagline: 'An offline-first Q&A learning platform for Nigerian students.',
    chips: ['React 19', 'React Router', 'Prisma', 'Nx'],
    href: null,
  },
  {
    kind: 'Product',
    period: '2025 — present',
    name: 'valideate',
    tagline: 'An evidence-first workspace that helps founders validate ideas.',
    chips: ['React', 'TypeScript', 'RAG', 'evals'],
    href: null,
  },
  {
    kind: 'Product · Live',
    period: '2025 — present',
    name: 'pitch-prophet',
    tagline: 'A football research terminal built around one honest daily loop.',
    chips: ['TensorFlow.js', 'Anthropic', 'SQLite'],
    href: null,
  },
  {
    kind: 'Platform · Founder',
    period: '2024 — present',
    name: '@veraclins-dev',
    tagline: 'The 12+ package Nx monorepo that everything I build runs on.',
    chips: ['Nx', 'React 19', 'Vite', 'Tailwind'],
    href: null,
  },
];

const caseStudies: Record<string, CaseStudy> = {
  valueblue: {
    kind: 'Enterprise SaaS',
    title: 'ValueBlue',
    tagline:
      'A BPMN 2.0 process-modeling editor and AI assistant, built for enterprise architects.',
    meta: 'Senior Frontend Engineer · 2022 — 2025 · Utrecht, NL',
    metrics: [
      { value: '20%+', label: 'fewer support tickets' },
      { value: '40%+', label: 'survey adoption lift' },
      { value: '7', label: 'microfrontends served' },
    ],
    blocks: [
      {
        heading: 'The context',
        body: 'ValueBlue builds an enterprise-architecture and process-modeling SaaS used by large organizations. The legacy modeling tools could not keep up with BPMN 2.0 expectations or the sheer volume of objects enterprise teams manage day to day.',
      },
      {
        heading: 'What I worked on',
        body: 'As part of the frontend team I contributed across several flagship initiatives:',
        bullets: [
          'A new BPMN 2.0 editor built from scratch on bpmn-js — diagram lifecycle, BPMN/PDF/PNG/SVG export, validation, and custom rules linking diagram elements to repository objects.',
          'End-to-end UI/UX for an in-app AI Assistant (knowledge and process help), from chat flows to response rendering and empty/error states.',
          'An advanced repositories datatable: bulk inline editing, saved filters, column management, and CSV/Excel export.',
          'A redesigned Surveys feature that replaced fragile guest links with a governed, multi-recipient flow.',
        ],
      },
      {
        heading: 'How it turned out',
        body: 'The AI assistant reduced support tickets by 20%+ within three months, the redesigned Surveys feature lifted adoption by 40%+, and the repositories work helped retain an at-risk enterprise account. Throughout, I maintained the Material-UI and Storybook component library shared across seven microfrontends.',
      },
    ],
    stack: [
      'TypeScript',
      'React',
      'React Router',
      'bpmn-js',
      'Single-spa',
      'Material UI',
      'Storybook',
    ],
  },
  'ishi-health': {
    kind: 'Healthcare',
    title: 'Ishi Health',
    tagline:
      'A zero-downtime database migration and a 3× faster app for a healthcare platform.',
    meta: 'Senior Software Engineer (consultant) · 2021 — 2025 · Remote',
    metrics: [
      { value: '0', label: 'downtime during migration' },
      { value: '3×', label: 'faster app' },
      { value: '30%+', label: 'infra cost reduced' },
    ],
    blocks: [
      {
        heading: 'The context',
        body: 'A healthcare platform was running on an aging data layer that could no longer keep up. It needed to move to a stronger database without ever going offline, and the app around it needed a serious performance overhaul.',
      },
      {
        heading: 'What I worked on',
        body: 'I led a two-way-sync database migration that kept both systems consistent under live traffic, so no data was lost and the platform stayed online throughout. Alongside it I ran a frontend performance overhaul that made the app substantially faster to load and use.',
      },
      {
        heading: 'How it turned out',
        body: 'The migration completed with zero downtime, the app came out roughly 3× faster, and the leaner data layer cut infrastructure cost by 30%+.',
      },
    ],
    stack: ['TypeScript', 'PostgreSQL', 'TypeORM', 'Vite'],
  },
};

export const caseStudySlugs = Object.keys(caseStudies);

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return caseStudies[slug];
}
